import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Brain, TrendingUp, AlertTriangle, Lightbulb, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";

interface Advice {
  id: string;
  content: string;
  timestamp: Date;
  applied: boolean;
  type: "briefing" | "alert" | "strategy";
}

const ADVICE_PROMPTS = {
  briefing: "Tu es Grafton, assistant stratégique pour dirigeants d'entreprise. Génère un briefing du jour concis mais complet : 1) Priorité #1 du jour 2) Point de vigilance 3) Opportunité à saisir 4) Action rapide recommandée. Utilise le markdown avec des emojis. Sois direct et actionnable.",
  alert: "Tu es Grafton, assistant stratégique. Analyse la situation type d'une PME et identifie 3 alertes critiques potentielles : trésorerie, concurrence, réglementation, RH. Pour chaque alerte, donne le risque et une action corrective immédiate. Format markdown avec emojis.",
  strategy: "Tu es Grafton, assistant stratégique. Propose 3 conseils stratégiques innovants pour accélérer la croissance d'une PME cette semaine. Inclus : marketing digital, optimisation commerciale, développement réseau. Format markdown structuré avec emojis.",
};

export function AIAdviceTab() {
  const { user } = useAuth();
  const [advices, setAdvices] = useState<Advice[]>([]);
  const [loading, setLoading] = useState<string | null>(null);

  const generateAdvice = async (type: keyof typeof ADVICE_PROMPTS) => {
    if (!user) return;
    setLoading(type);
    try {
      const { data, error } = await supabase.functions.invoke("grafton-chat", {
        body: {
          messages: [
            { role: "system", content: ADVICE_PROMPTS[type] },
            { role: "user", content: type === "briefing" ? "Donne-moi mon briefing du jour." : type === "alert" ? "Quelles sont les alertes à surveiller ?" : "Quels conseils stratégiques me recommandes-tu ?" }
          ]
        }
      });
      if (error) throw error;

      const content = data?.choices?.[0]?.message?.content || data?.content || "Aucune réponse reçue.";
      setAdvices(prev => [{
        id: crypto.randomUUID(),
        content,
        timestamp: new Date(),
        applied: false,
        type,
      }, ...prev]);
      toast.success("Conseil généré par Grafton");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la génération");
    }
    setLoading(null);
  };

  const toggleApplied = (id: string) => {
    setAdvices(prev => prev.map(a => a.id === id ? { ...a, applied: !a.applied } : a));
  };

  const typeConfig = {
    briefing: { icon: Brain, label: "Briefing", color: "bg-primary/10 text-primary border-primary/20" },
    alert: { icon: AlertTriangle, label: "Alerte", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
    strategy: { icon: TrendingUp, label: "Stratégie", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-foreground">Conseils stratégiques IA</h2>
        <p className="text-sm text-muted-foreground">Grafton analyse votre situation et propose des recommandations personnalisées</p>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card
          className="cursor-pointer hover:border-primary/40 transition-colors border-border/50"
          onClick={() => !loading && generateAdvice("briefing")}
        >
          <CardContent className="py-5 px-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Brain className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Briefing du jour</p>
              <p className="text-xs text-muted-foreground">Synthèse et priorités</p>
            </div>
            {loading === "briefing" && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-amber-500/40 transition-colors border-border/50"
          onClick={() => !loading && generateAdvice("alert")}
        >
          <CardContent className="py-5 px-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Alertes intelligentes</p>
              <p className="text-xs text-muted-foreground">Risques et vigilances</p>
            </div>
            {loading === "alert" && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-emerald-500/40 transition-colors border-border/50"
          onClick={() => !loading && generateAdvice("strategy")}
        >
          <CardContent className="py-5 px-4 flex items-center gap-4">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <TrendingUp className="h-5 w-5 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm text-foreground">Conseils stratégiques</p>
              <p className="text-xs text-muted-foreground">Croissance et opportunités</p>
            </div>
            {loading === "strategy" && <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />}
          </CardContent>
        </Card>
      </div>

      {/* Advice feed */}
      {advices.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Sparkles className="h-10 w-10 mx-auto mb-4 text-muted-foreground/30" />
            <p className="text-muted-foreground font-medium">Aucun conseil généré</p>
            <p className="text-xs text-muted-foreground mt-1">Cliquez sur l'un des boutons ci-dessus pour demander un conseil à Grafton</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {advices.map(advice => {
            const config = typeConfig[advice.type];
            const Icon = config.icon;
            return (
              <Card key={advice.id} className={`transition-all ${advice.applied ? "opacity-60" : ""}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={`text-xs ${config.color}`}>
                      <Icon className="h-3 w-3 mr-1" />{config.label}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {advice.timestamp.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <Button
                    variant={advice.applied ? "secondary" : "outline"}
                    size="sm"
                    className="text-xs gap-1"
                    onClick={() => toggleApplied(advice.id)}
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    {advice.applied ? "Appliqué" : "Marquer appliqué"}
                  </Button>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90">
                    <ReactMarkdown>{advice.content}</ReactMarkdown>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
